import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { api } from "../lib/api";
import { isV2 } from "../lib/config";

export type ColKind = "text" | "money" | "int" | "percent";
export interface ReportCol { key: string; header: string; kind: ColKind }
export interface ReportSection { key: string; title: string; columns: ReportCol[]; rows: Array<Record<string, string | number | null>>; totals?: boolean; chart?: { label: string; value: string }; note?: string }
export interface ReportKpi { key: string; label: string; value: number | string; kind: ColKind | "text"; tone?: "good" | "bad" | "warn" }
export interface ReportView { id: string; title: string; subtitle: string; period: { from: string; to: string }; kpis: ReportKpi[]; sections: ReportSection[]; notes: string[] }

export const REPORTS = ["pnl", "sales", "tax", "shifts", "profit", "aging", "wallet", "staff", "visits", "inventory", "daybook"] as const;
export type ReportId = (typeof REPORTS)[number];
export const ICONS: Record<ReportId, string> = { pnl: "🏦", sales: "💰", tax: "🧾", shifts: "🏧", profit: "📈", aging: "⏳", wallet: "💳", staff: "👥", visits: "🎢", inventory: "📦", daybook: "📒" };
/** reports whose figures are "as of today", not for the period */
export const AS_OF: ReportId[] = ["aging"];

export const reportQuery = (from: string, to: string, scope: "branch" | "all", locale: string) => `from=${from}&to=${to}&scope=${scope}&locale=${locale}`;
export const useReportInfo = () => useQuery({ queryKey: ["reports", "info"], enabled: isV2(), queryFn: () => api.get<{ canAllBranches: boolean }>("/reports") });
export const useReport = (id: ReportId, from: string, to: string, scope: "branch" | "all", locale: string) =>
  useQuery({
    queryKey: ["reports", id, from, to, scope, locale], enabled: isV2() && from <= to, placeholderData: keepPreviousData,
    queryFn: async () => (await api.get<{ report: ReportView }>(`/reports/${id}?${reportQuery(from, to, scope, locale)}`)).report,
  });

/** Local-calendar day, YYYY-MM-DD (the shop's own day, not UTC). */
export const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
export function presetRange(p: "today" | "yesterday" | "week" | "month" | "lastMonth" | "quarter" | "year", now = new Date()): { from: string; to: string } {
  const d = (y: number, m: number, day: number) => new Date(y, m, day);
  const y = now.getFullYear(), m = now.getMonth(), day = now.getDate();
  switch (p) {
    case "today": return { from: ymd(now), to: ymd(now) };
    case "yesterday": return { from: ymd(d(y, m, day - 1)), to: ymd(d(y, m, day - 1)) };
    case "week": { const back = (now.getDay() + 1) % 7; return { from: ymd(d(y, m, day - back)), to: ymd(now) }; } // the week starts on Saturday
    case "month": return { from: ymd(d(y, m, 1)), to: ymd(now) };
    case "lastMonth": return { from: ymd(d(y, m - 1, 1)), to: ymd(d(y, m, 0)) };
    case "quarter": return { from: ymd(d(y, Math.floor(m / 3) * 3, 1)), to: ymd(now) };
    case "year": return { from: ymd(d(y, 0, 1)), to: ymd(now) };
  }
}
